import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Phone, Mail, MessageCircle, Bot, Zap, Check, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Products = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const products = [
    {
      id: 'sms',
      icon: MessageSquare,
      title: 'SMS',
      subtitle: 'Reliable Bulk Messaging at Scale',
      description: 'Reach your customers instantly with our enterprise-grade SMS platform. Deliver OTPs, alerts, promotional messages, and transactional notifications with guaranteed high deliverability across global networks.',
      image: 'https://images.pexels.com/photos/3943951/pexels-photo-3943951.jpeg?w=800',
      features: [
        'Global coverage in 190+ countries',
        'Delivery rate of 99.5%+',
        'Two-way messaging support',
        'Unicode and regional language support',
        'Real-time delivery reports',
        'Smart routing for cost optimization',
        'DND scrubbing and compliance',
        'Bulk upload via API, Excel, or portal'
      ],
      useCases: ['OTP & Authentication', 'Transaction Alerts', 'Marketing Campaigns', 'Service Notifications']
    },
    {
      id: 'whatsapp',
      icon: MessageCircle,
      title: 'WhatsApp Business',
      subtitle: 'Official WhatsApp Business API',
      description: 'Connect with customers on their favorite messaging platform. Leverage WhatsApp Business API for rich, interactive conversations with verified business accounts, template messages, and multimedia support.',
      image: 'https://images.pexels.com/photos/46924/pexels-photo-46924.jpeg?w=800',
      features: [
        'Verified green tick business account',
        'Rich media messaging (images, videos, documents)',
        'Interactive buttons and quick replies',
        'Template message approval system',
        'Chatbot integration',
        'End-to-end encryption',
        'Read receipts and delivery status',
        'Customer support and notifications'
      ],
      useCases: ['Customer Support', 'Order Updates', 'Appointment Booking', 'Marketing Campaigns']
    },
    {
      id: 'voice',
      icon: Phone,
      title: 'Voice',
      subtitle: 'Cloud Telephony & IVR Solutions',
      description: 'Deliver crystal-clear voice communication with our cloud telephony platform. Build sophisticated IVR systems, make automated calls, and engage customers with personalized voice messages at scale.',
      image: 'https://images.pexels.com/photos/5206291/pexels-photo-5206291.jpeg?w=800',
      features: [
        'HD voice quality',
        'Multi-level IVR builder',
        'Text-to-speech in multiple languages',
        'Call recording and monitoring',
        'Click-to-call integration',
        'Virtual numbers in 50+ countries',
        'Automated outbound calling',
        'Real-time call analytics'
      ],
      useCases: ['Customer Verification', 'IVR Systems', 'Telemarketing', 'Emergency Alerts']
    },
    {
      id: 'rcs',
      icon: Zap,
      title: 'RCS',
      subtitle: 'Rich Communication Services',
      description: 'Experience the future of messaging with RCS. Send interactive, branded messages with rich media, carousels, suggested actions, and tracking—directly within the native messaging app.',
      image: 'https://images.pexels.com/photos/607812/pexels-photo-607812.jpeg?w=800',
      features: [
        'Rich media cards and carousels',
        'Branded sender identity',
        'Interactive buttons and actions',
        'Read receipts and typing indicators',
        'Location sharing',
        'High-resolution images and videos',
        'Product catalogs',
        'Delivery and engagement analytics'
      ],
      useCases: ['Product Showcases', 'Booking Confirmations', 'Interactive Surveys', 'Brand Campaigns']
    },
    {
      id: 'email',
      icon: Mail,
      title: 'Email',
      subtitle: 'Enterprise Email Delivery Platform',
      description: 'Send transactional and marketing emails with confidence. Our enterprise email platform ensures high deliverability, provides detailed analytics, and supports advanced template management.',
      image: 'https://images.pexels.com/photos/840996/pexels-photo-840996.jpeg?w=800',
      features: [
        'High deliverability (98%+)',
        'SMTP and API integration',
        'Advanced template builder',
        'A/B testing capabilities',
        'Real-time bounce management',
        'Spam score checking',
        'Detailed engagement analytics',
        'Automated email workflows'
      ],
      useCases: ['Transactional Emails', 'Marketing Campaigns', 'Newsletters', 'Password Resets']
    },
    {
      id: 'voice-bots',
      icon: Bot,
      title: 'Voice Bots',
      subtitle: 'AI-Powered Voice Assistants',
      description: 'Automate customer interactions with intelligent voice bots. Powered by advanced AI and natural language processing, handle customer queries, bookings, and support calls 24/7.',
      image: 'https://images.pexels.com/photos/18069697/pexels-photo-18069697.png?w=800',
      features: [
        'Natural language understanding',
        'Multi-language support',
        'Speech recognition and synthesis',
        'Sentiment analysis',
        'Custom workflows and logic',
        'CRM and system integration',
        'Call transfer to human agents',
        'Performance analytics'
      ],
      useCases: ['Customer Support', 'Appointment Scheduling', 'Lead Qualification', 'Surveys & Feedback']
    },
    {
      id: 'whatsapp-bots',
      icon: Bot,
      title: 'WhatsApp Bots',
      subtitle: 'Conversational AI for WhatsApp',
      description: 'Deploy intelligent WhatsApp chatbots for automated customer engagement. Handle inquiries, process orders, provide support, and drive conversions through conversational AI.',
      image: 'https://images.pexels.com/photos/18069696/pexels-photo-18069696.png?w=800',
      features: [
        'AI-powered conversation flows',
        'Natural language processing',
        'Multi-language support',
        'Rich media responses',
        'E-commerce integration',
        'Payment gateway support',
        'Human agent handoff',
        'Conversation analytics'
      ],
      useCases: ['E-commerce Sales', 'Customer Service', 'Lead Generation', 'Order Tracking']
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-cyan-500/20 via-transparent to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our Communication Products
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Comprehensive suite of communication APIs and tools designed to power your customer engagement across multiple channels
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {products.map((product, index) => (
              <div 
                key={product.id} 
                id={product.id}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center scroll-mt-24`}
              >
                {/* Image */}
                <div className="w-full lg:w-1/2">
                  <div className="relative group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-3xl blur opacity-25 group-hover:opacity-40 transition duration-500" />
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                      <img 
                        src={product.image} 
                        alt={product.title}
                        className="w-full h-[400px] object-cover transform group-hover:scale-105 transition duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                      <div className="absolute bottom-6 left-6">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg">
                          <product.icon className="h-8 w-8 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="w-full lg:w-1/2 space-y-6">
                  <div>
                    <h2 className="text-4xl font-bold text-slate-900 mb-2">{product.title}</h2>
                    <p className="text-xl text-blue-600 font-semibold mb-4">{product.subtitle}</p>
                    <p className="text-lg text-slate-700 leading-relaxed">{product.description}</p>
                  </div>

                  {/* Features */}
                  <div className="space-y-4">
                    <h3 className="text-xl font-bold text-slate-900">Key Features</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {product.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-1">
                            <div className="w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center">
                              <Check className="h-3 w-3 text-white" />
                            </div>
                          </div>
                          <span className="text-slate-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Use Cases */}
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">Use Cases</h3>
                    <div className="flex flex-wrap gap-2">
                      {product.useCases.map((useCase, idx) => (
                        <span 
                          key={idx} 
                          className="px-4 py-2 bg-white border-2 border-blue-200 text-blue-700 rounded-full font-medium text-sm shadow-sm"
                        >
                          {useCase}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4">
                    <Link to="/contact">
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                        Get Started with {product.title}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-blue-900 via-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-slate-200 mb-10 leading-relaxed">
            Choose the right communication channels for your business and start engaging with customers today
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-white px-10 py-7 text-lg rounded-xl shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300 hover:scale-105">
              Contact Our Team
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Products;